import { useMemo } from "react";

import { Image, Row, Tooltip, Icon, Text, TextVariant } from "@bridgesplit/ui";
import { useMarketOracleInfos, MarketAssetView } from "@bridgesplit/abf-react";
import { OracleProvider } from "@bridgesplit/abf-sdk";
import { getOracleTypes, useOracleStats } from "app/components/oracles/useOracleStats";
import { Box } from "@mui/material";

import { getOracleMeta } from "../common/oracle";
import OracleTooltipTitle from "./OracleTooltipTitle";

export default function OracleLogos({
    principalMint,
    collateralMint,
    marketInformation,
    variant
}: {
    principalMint: string | undefined;
    collateralMint: string | undefined;
    marketInformation: string | undefined;
    variant: TextVariant;
}) {
    const { getMarketInformationByAddressAndMints, getMarketInformationByPrincipalMint } = useMarketOracleInfos({
        filter: {
            principalMints: principalMint ? [principalMint] : [],
            addresses: marketInformation ? [marketInformation] : []
        }
    });

    const collateralAssetMarketInfo = getMarketInformationByAddressAndMints(
        marketInformation,
        collateralMint,
        principalMint
    );

    const principalAssetMarketInfo = useMemo((): MarketAssetView | undefined => {
        const fromSpecific = getMarketInformationByAddressAndMints(marketInformation, principalMint, principalMint);
        if (fromSpecific) return fromSpecific;

        const markets = getMarketInformationByPrincipalMint(principalMint);
        const defaultMarket = markets?.find((m) => m.marketInformation.isDefault);
        if (!defaultMarket) return undefined;
        const asset = defaultMarket.marketInformationAssetInfo.find((a) => a.assetIdentifier === principalMint);
        if (!asset) return undefined;
        return { market: defaultMarket.marketInformation, asset, customOracles: [] };
    }, [getMarketInformationByAddressAndMints, getMarketInformationByPrincipalMint, marketInformation, principalMint]);

    const allOracleTypes = getOracleTypes({
        collateralMarketData: collateralAssetMarketInfo,
        principalMarketData: principalAssetMarketInfo
    });

    const oracleStats = useOracleStats({
        collateralMarketData: collateralAssetMarketInfo,
        principalMarketData: principalAssetMarketInfo,
        variant: variant
    });

    return (
        <Tooltip
            minTooltipWidth="175px"
            title={
                <OracleTooltipTitle
                    topLevelOracleStats={oracleStats?.topLevelOracleStats}
                    customOracleStats={oracleStats?.customOracleStats}
                    variant={variant}
                />
            }
            reverseColors
        >
            <Row spacing={1}>
                <Text variant={variant} svgColor="caption">
                    <Icon type="tooltip" />
                </Text>
                <LayeredOracleLogos allOracles={allOracleTypes} />
            </Row>
        </Tooltip>
    );
}

function LayeredOracleLogos({ allOracles }: { allOracles: OracleProvider[] }) {
    return (
        <Box sx={{ position: "relative", display: "flex" }}>
            {allOracles.map((oracleProvider, index) => {
                const oracleMeta = getOracleMeta(oracleProvider);
                return (
                    <Image
                        key={oracleProvider}
                        variant="circle"
                        size="16px"
                        src={oracleMeta?.image}
                        sx={{
                            marginLeft: index > 0 ? "-4px" : 0,
                            zIndex: allOracles.length - index,
                            position: "relative"
                        }}
                    />
                );
            })}
        </Box>
    );
}
